import { useEffect, useMemo, useState } from 'react';
import { ExpiryStatus } from '../../../../../Generated/Commodity-Management-Api';
import { GroupingOption } from '../../../../../Types';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import { StoneXAutocomplete, StoneXButton, StoneXCheckbox, StoneXSelect, StoneXTextField } from '../../../../StoneXMui';
import { StoneXTransferList } from '../../../../StoneXMui/StoneXTransferList/StoneXTransferList';
import { difference } from 'lodash';
import { PositionColumns, PositionReportColumn, PositionReportConfiguration } from '../../Reports/Reports/PositionReport/Types';
import { PositionLikeReportSettingsPageProps } from './types';

const DEFAULT_POSITION_COLUMNS = Object.keys(PositionColumns) as PositionReportColumn[];
const DEFAULT_AVAILABLE_COLUMNS: PositionReportColumn[] = [
  'BudgetMonth',
  'TradeDate',
  'Commodity',
  'Counterparty',
  'Portfolio',
  'ContractMonth',
  'Side',
  'DealTicket',
  'Volume',
  'Currency',
  'TradePrice',
  'PositionType',
  'SettlementType',
  'SettlementDate',
  'PositionTypeOrOption',
  'OptionType',
  'OptionStyle',
  'OptionStrike',
  'ScenarioMarketPrice',
  'ScenarioMtmPrice',
  'ScenarioMtmValue',
  'MarketPrice',
  'MtmPrice',
  'MtmValue',
  'Actions',
];
const DEFAULT_ACTIVE_COLUMNS: PositionReportColumn[] = [
  'BudgetMonth',
  'ContractMonth',
  'Commodity',
  'TradeDate',
  'DealTicket',
  'SettlementType',
  'SettlementDate',
  'PositionTypeOrOption',
  'Volume',
  'TradePrice',
  'MarketPrice',
  'MtmPrice',
  'MtmValue',
];

function isPositionColumn(column: string): column is PositionReportColumn {
	return column in PositionColumns;
}

function getUniqueKnownPositionColumns(columns: string[]): PositionReportColumn[] {
	const seen = new Set<PositionReportColumn>();
	return columns.reduce((result, column) => {
		if (!isPositionColumn(column) || seen.has(column)) {
			return result;
		}

		seen.add(column);
		result.push(column);
		return result;
	}, [] as PositionReportColumn[]);
}

function getInitialAvailableColumns(configuration: PositionReportConfiguration): PositionReportColumn[] {
	if (!configuration.columns?.available?.length) {
		return DEFAULT_AVAILABLE_COLUMNS;
	}

	const sanitizedAvailableColumns = getUniqueKnownPositionColumns(configuration.columns.available);
	return sanitizedAvailableColumns.length ? sanitizedAvailableColumns : DEFAULT_AVAILABLE_COLUMNS;
}

function getInitialActiveColumns(configuration: PositionReportConfiguration, availableColumns: PositionReportColumn[]): PositionReportColumn[] {
	const configuredOrDefaultActive = configuration.columns?.active?.length
		? getUniqueKnownPositionColumns(configuration.columns.active)
		: DEFAULT_ACTIVE_COLUMNS;
  return configuredOrDefaultActive.filter((column) => availableColumns.includes(column));
}

function parsePositionReportConfiguration(configurationJson: string): PositionReportConfiguration {
  return JSON.parse(configurationJson) as PositionReportConfiguration;
}

export default function PositionReportSettingsPage({ report, groupingOptions, isSaving, onDeleteReport, onUpdateReport }: PositionLikeReportSettingsPageProps) {

  const initialConfiguration = useMemo(() => parsePositionReportConfiguration(report.configuration), [report.configuration]);
  const initialAvailableColumns = useMemo(() => getInitialAvailableColumns(initialConfiguration), [initialConfiguration]);
  const initialActiveColumns = useMemo(
	() => getInitialActiveColumns(initialConfiguration, initialAvailableColumns),
	[initialConfiguration, initialAvailableColumns],
  );

  const [configuration, setConfiguration] = useState<PositionReportConfiguration>(initialConfiguration);
	const [reportName, setReportName] = useState<string>(report.reportName);
  const [availableColumns, setAvailableColumns] = useState<PositionReportColumn[]>(initialAvailableColumns);
  const [unavailableColumns, setUnavailableColumns] = useState<PositionReportColumn[]>(difference(DEFAULT_POSITION_COLUMNS, initialAvailableColumns));
  const [activeColumns, setActiveColumns] = useState<PositionReportColumn[]>(initialActiveColumns);
  const [inactiveAvailableColumns, setInactiveAvailableColumns] = useState<PositionReportColumn[]>(difference(initialAvailableColumns, initialActiveColumns));
  const [showQuoteDate, setShowQuoteDate] = useState<boolean>(initialConfiguration.inputs?.quoteDate?.show ?? false);
  const [showBudgetStartDate, setShowBudgetStartDate] = useState<boolean>(initialConfiguration.inputs?.budgetStartDate?.show ?? false);
  const [showBudgetEndDate, setShowBudgetEndDate] = useState<boolean>(initialConfiguration.inputs?.budgetEndDate?.show ?? false);
  const [netOptionPremiums, setNetOptionPremiums] = useState<boolean>(initialConfiguration.behavior?.netOptionPremiums ?? false);
  const [level1GroupingOption, setLevel1GroupingOption] = useState<GroupingOption | null>(initialConfiguration.behavior?.grouping?.[0] ?? null);
  const [level2GroupingOption, setLevel2GroupingOption] = useState<GroupingOption | null>(initialConfiguration.behavior?.grouping?.[1] ?? null);
  const [expiryStatus, setExpiryStatus] = useState<ExpiryStatus>(initialConfiguration.behavior?.expiryStatus ?? ExpiryStatus.Expired);

  const positionColumnOrderMap = useMemo<{ [key: string]: number }>(() => {
	const keys = Object.keys(PositionColumns);
	const map: { [key: string]: number } = {};
	keys.forEach((value: string, index) => (map[value] = index));
	return map;
  }, []);

  useEffect(() => {
	const parsedConfiguration = parsePositionReportConfiguration(report.configuration);
	const currentAvailableColumns = getInitialAvailableColumns(parsedConfiguration);
	const currentUnavailableColumns = difference(DEFAULT_POSITION_COLUMNS, currentAvailableColumns);
	const currentActiveColumns = getInitialActiveColumns(parsedConfiguration, currentAvailableColumns);
	const currentInactiveAvailableColumns = difference(currentAvailableColumns, currentActiveColumns);

	setConfiguration(parsedConfiguration);
	setReportName(report.reportName);
	setAvailableColumns(currentAvailableColumns);
	setUnavailableColumns(currentUnavailableColumns);
	setActiveColumns(currentActiveColumns);
	setInactiveAvailableColumns(currentInactiveAvailableColumns);
	setShowQuoteDate(parsedConfiguration.inputs?.quoteDate?.show ?? true);
	setShowBudgetStartDate(parsedConfiguration.inputs?.budgetStartDate?.show ?? false);
	setShowBudgetEndDate(parsedConfiguration.inputs?.budgetEndDate?.show ?? false);
	setNetOptionPremiums(parsedConfiguration.behavior?.netOptionPremiums ?? false);
	setLevel1GroupingOption(parsedConfiguration.behavior?.grouping?.[0] ?? 'BudgetMonth');
	setLevel2GroupingOption(parsedConfiguration.behavior?.grouping?.[1] ?? null);
	setExpiryStatus(parsedConfiguration.behavior?.expiryStatus ?? ExpiryStatus.Open);
  }, [report]);

  function onAvailableColumnsUpdate(updatedUnavailableColumns: PositionReportColumn[], updatedAvailableColumns: PositionReportColumn[]) {
	const filteredActiveColumns = activeColumns.filter((column) => updatedAvailableColumns.includes(column));
	const updatedInactiveAvailableColumns = difference(updatedAvailableColumns, filteredActiveColumns);

	setUnavailableColumns(updatedUnavailableColumns);
	setAvailableColumns(updatedAvailableColumns);
	setActiveColumns(filteredActiveColumns);
	setInactiveAvailableColumns(updatedInactiveAvailableColumns);
  }

  function onActiveColumnsUpdate(updatedInactiveAvailableColumns: PositionReportColumn[], updatedActiveColumns: PositionReportColumn[]) {
	setInactiveAvailableColumns(updatedInactiveAvailableColumns);
	setActiveColumns(updatedActiveColumns);
  }

  function resetConfiguration() {
	const parsedConfiguration = parsePositionReportConfiguration(report.configuration);
	const currentAvailableColumns = getInitialAvailableColumns(parsedConfiguration);
	const currentUnavailableColumns = difference(DEFAULT_POSITION_COLUMNS, currentAvailableColumns);
	const currentActiveColumns = getInitialActiveColumns(parsedConfiguration, currentAvailableColumns);
	const currentInactiveAvailableColumns = difference(currentAvailableColumns, currentActiveColumns);

	setConfiguration(parsedConfiguration);
	setReportName(report.reportName);
	setAvailableColumns(currentAvailableColumns);
	setUnavailableColumns(currentUnavailableColumns);
	setActiveColumns(currentActiveColumns);
	setInactiveAvailableColumns(currentInactiveAvailableColumns);
	setShowQuoteDate(parsedConfiguration.inputs?.quoteDate?.show ?? true);
	setShowBudgetStartDate(parsedConfiguration.inputs?.budgetStartDate?.show ?? false);
	setShowBudgetEndDate(parsedConfiguration.inputs?.budgetEndDate?.show ?? false);
	setNetOptionPremiums(parsedConfiguration.behavior?.netOptionPremiums ?? false);
	setLevel1GroupingOption(parsedConfiguration.behavior?.grouping?.[0] ?? 'BudgetMonth');
	setLevel2GroupingOption(parsedConfiguration.behavior?.grouping?.[1] ?? null);
	setExpiryStatus(parsedConfiguration.behavior?.expiryStatus ?? ExpiryStatus.Open);
  }

  function saveConfiguration() {
	const grouping = [level1GroupingOption, level2GroupingOption].filter((x) => x !== null) as GroupingOption[];

	const nextConfiguration: PositionReportConfiguration = {
	  ...configuration,
	  inputs: {
		quoteDate: { show: showQuoteDate },
		budgetStartDate: { show: showBudgetStartDate },
		budgetEndDate: { show: showBudgetEndDate },
	  },
	  behavior: {
		...configuration.behavior,
		netOptionPremiums,
		expiryStatus,
		grouping,
	  },
	  columns: {
		available: availableColumns,
		active: activeColumns,
	  },
	};

	onUpdateReport({
	  ...report,
	  reportName: reportName.trim(),
	  configuration: JSON.stringify(nextConfiguration),
	});

	setConfiguration(nextConfiguration);
  }

  return (
	<div>
	  <StoneXRow>
		<StoneXTextField
			value={reportName}
			label="Report Name"
			onUpdate={setReportName}
		/>
	  </StoneXRow>

		<StoneXDivider />

		<StoneXRow align='start'>
			<div style={{ flex: '1 1' }}>
				
				<StoneXRow>
					<h3>Inputs</h3>
				</StoneXRow>

				<StoneXRow>
				<StoneXCheckbox
					label="Show Quote Date"
					helperText='Show quote date selector (uses last quote date as default)'
					checked={showQuoteDate}
					onUpdate={setShowQuoteDate}
				/>
				</StoneXRow>
				<StoneXRow>
				<StoneXCheckbox
					label="Show Budget Start Date"
					helperText='Whether to show or hide start budget date'
					checked={showBudgetStartDate}
					onUpdate={setShowBudgetStartDate}
				/>
				</StoneXRow>
				<StoneXRow>
				<StoneXCheckbox
					label="Show Budget End Date"
					helperText='Whether to show or hide end budget date'
					checked={showBudgetEndDate}
					onUpdate={setShowBudgetEndDate}
				/>
				</StoneXRow>
			</div>

			<div style={{ flex: '1 1' }}>
				<StoneXRow>
					<h3>Behavior</h3>
				</StoneXRow>
				<StoneXRow>
				<StoneXAutocomplete
					label="Group By (Optional)"
					helperText="The first grouping level"
					options={groupingOptions}
					value={level1GroupingOption}
					onUpdate={setLevel1GroupingOption}
					getOptionLabel={(x) => x}
				/>
				</StoneXRow>

				<StoneXRow>
				<StoneXAutocomplete
					label="Then By (Optional)"
					helperText="The second grouping level"
					options={groupingOptions}
					value={level2GroupingOption}
					onUpdate={setLevel2GroupingOption}
					getOptionLabel={(x) => x}
				/>
				</StoneXRow>

				<StoneXRow>
				<StoneXSelect
					label="Expiry Status"
					helperText="Whether to include expired months, open months, or all months"
					options={[ExpiryStatus.All, ExpiryStatus.Expired, ExpiryStatus.Open]}
					getId={(x) => x}
					getOptionLabel={(x) => x}
					value={expiryStatus}
					onChange={(value) => setExpiryStatus(value ?? ExpiryStatus.Expired)}
				/>
				</StoneXRow>

				<StoneXRow>
				<StoneXCheckbox
					label="Net Option Premiums"
					helperText="Whether to show calcuations including option premium costs in summary"
					checked={netOptionPremiums}
					onUpdate={setNetOptionPremiums}
				/>
				</StoneXRow>
			</div>
	  </StoneXRow>

	  <StoneXDivider />

		<StoneXRow>
	  	<h3>Columns</h3>
		</StoneXRow>

		<StoneXRow align='start'>
			<div style={{ flex: '1 1' }}>
				<p>Step 1: Choose which columns are available to the customer.</p>
				<StoneXTransferList
				leftList={unavailableColumns}
				leftHeader="Unavailable Columns"
				rightList={availableColumns}
				rightHeader="Available Columns"
				getId={(x) => PositionColumns[x]?.colId ?? x}
				getGroup={(x) => PositionColumns[x]?.parentHeader}
				getSortingOrder={(x) => positionColumnOrderMap[x]}
				getOptionLabel={(x) => PositionColumns[x]?.columnName ?? PositionColumns[x]?.headerName ?? x}
				onUpdate={onAvailableColumnsUpdate}
				/>
			</div>

			<div style={{ flex: '1 1' }}>
				<p>Step 2: From available columns, choose which ones are active by default.</p>
				<StoneXTransferList
				leftList={inactiveAvailableColumns}
				leftHeader="Available but Inactive"
				rightList={activeColumns}
				rightHeader="Active Columns"
				getId={(x) => PositionColumns[x]?.colId ?? x}
				getGroup={(x) => PositionColumns[x]?.parentHeader}
				getSortingOrder={(x) => positionColumnOrderMap[x]}
				getOptionLabel={(x) => PositionColumns[x]?.columnName ?? PositionColumns[x]?.headerName ?? x}
				onUpdate={onActiveColumnsUpdate}
				/>
			</div>
	  </StoneXRow>

	  <StoneXRow justify="end" marginDirection="top">
		<StoneXButton variant="outlined" onClick={() => onDeleteReport(report.customerReportId)}>
		  Delete Report
		</StoneXButton>
		<StoneXButton variant="outlined" onClick={resetConfiguration}>
		  Reset
		</StoneXButton>
		<StoneXButton isWorking={isSaving} onClick={saveConfiguration}>Save</StoneXButton>
	  </StoneXRow>
	</div>
  );
}
